import { useState } from "react";
import { ghostBtnClass, inputClass } from "@/components/common/surfaceClasses";
import type { SsdDealerMapBoard, SsdMappedDealer } from "@/lib/ssdSalesClient";

export function OpsLocationsMappedList(props: {
  board: SsdDealerMapBoard;
  busy: boolean;
  onRemap: (row: SsdMappedDealer, sellingOrgId: string, locationId: string | null) => Promise<boolean>;
  onUnmap: (sourceDealerId: string) => Promise<boolean>;
}) {
  const mapped = props.board.mapped ?? [];
  if (mapped.length === 0) {
    return <p className="text-sm text-gray-600 dark:text-zinc-300">No saved host lot IDs yet. Unmapped lots appear above.</p>;
  }
  return (
    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700">
      {mapped.map((row) => (
        <MappedLotRow
          key={row.source_dealer_id}
          row={row}
          board={props.board}
          busy={props.busy}
          onRemap={props.onRemap}
          onUnmap={props.onUnmap}
        />
      ))}
    </ul>
  );
}

function MappedLotRow(props: {
  row: SsdMappedDealer;
  board: SsdDealerMapBoard;
  busy: boolean;
  onRemap: (row: SsdMappedDealer, sellingOrgId: string, locationId: string | null) => Promise<boolean>;
  onUnmap: (sourceDealerId: string) => Promise<boolean>;
}) {
  const { row, board } = props;
  const [open, setOpen] = useState(false);
  const [orgId, setOrgId] = useState(row.selling_org_id ?? board.subscribers[0]?.org_id ?? "");
  const [locationId, setLocationId] = useState(row.location_id ?? "");
  const org = board.subscribers.find((s) => s.org_id === orgId) ?? board.subscribers[0];
  const label = row.source_dealer_name || "Unnamed lot";
  const current = [row.selling_org_name, row.location_name].filter(Boolean).join(" · ") || "Org only";

  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium text-gray-900 dark:text-zinc-100">
            {label} <span className="font-mono text-xs text-gray-500">({row.source_dealer_id})</span>
          </p>
          <p className="text-xs text-gray-600 dark:text-zinc-400">{current}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={ghostBtnClass} disabled={props.busy} onClick={() => setOpen((v) => !v)}>
            {open ? "Cancel" : "Remap"}
          </button>
          <button
            type="button"
            className={`${ghostBtnClass} text-amber-900`}
            disabled={props.busy}
            onClick={() => void props.onUnmap(row.source_dealer_id)}
          >
            Unmap
          </button>
        </div>
      </div>
      {open ? (
        <div className="mt-2 space-y-2">
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Organization
            <select
              className={`mt-1 ${inputClass}`}
              value={org?.org_id ?? ""}
              onChange={(e) => {
                setOrgId(e.target.value);
                setLocationId("");
              }}
            >
              {board.subscribers.map((s) => (
                <option key={s.org_id} value={s.org_id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Location
            <select className={`mt-1 ${inputClass}`} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Org only</option>
              {(org?.locations ?? []).map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                  {loc.city ? ` · ${loc.city}` : ""}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={props.busy || !org}
            onClick={() => {
              if (!org) return;
              void props.onRemap(row, org.org_id, locationId || null).then((ok) => {
                if (ok) setOpen(false);
              });
            }}
          >
            {props.busy ? "Saving…" : "Save mapping"}
          </button>
        </div>
      ) : null}
    </li>
  );
}
